import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CrossTabSyncService } from './cross-tab-sync.service';

/**
 * InactivityTrackerService
 *
 * Monitors user activity (mouse, keyboard, touch events) and triggers callbacks
 * when inactivity thresholds are reached.
 *
 * Phase 5: Session Timeout Implementation
 * Phase 6: Cross-tab activity synchronization
 */
@Injectable({
  providedIn: 'root'
})
export class InactivityTrackerService {
  private timeoutDuration: number; // Total inactivity timeout in milliseconds
  private warningDuration: number; // Warning time before timeout in milliseconds
  private inactivityTimer: any;
  private warningTimer: any;
  private isTracking: boolean = false;

  // Callbacks
  private onWarning: (() => void) | null = null;
  private onTimeout: (() => void) | null = null;

  // Activity event types
  private readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  constructor(
    private ngZone: NgZone,
    private crossTabSync: CrossTabSyncService
  ) {
    // Load configuration from environment
    const sessionTimeoutMinutes = Number(environment.sessionTimeoutMinutes) || 59;
    const warningBeforeTimeoutMinutes = Number(environment.warningBeforeTimeoutMinutes) || 1;

    this.timeoutDuration = sessionTimeoutMinutes * 60 * 1000;
    this.warningDuration = (sessionTimeoutMinutes - warningBeforeTimeoutMinutes) * 60 * 1000;

    console.log('[Phase 5] InactivityTracker initialized:', {
      timeoutMinutes: sessionTimeoutMinutes,
      warningMinutes: warningBeforeTimeoutMinutes
    });
  }

  /**
   * Start tracking user activity
   * @param onWarning Callback when warning threshold is reached
   * @param onTimeout Callback when timeout threshold is reached
   */
  startTracking(onWarning: () => void, onTimeout: () => void): void {
    if (this.isTracking) {
      console.warn('[Phase 5] InactivityTracker already tracking');
      return;
    }

    this.onWarning = onWarning;
    this.onTimeout = onTimeout;
    this.isTracking = true;

    // Phase 6: Listen for activity events from other tabs
    this.crossTabSync.onActivity(() => {
      console.log('[Phase 6] Activity received from another tab, resetting timers');
      this.resetTimers();
    });

    // Register activity listeners outside Angular zone for performance
    this.ngZone.runOutsideAngular(() => {
      this.ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, this.handleActivity, true);
      });
    });

    // Start timers
    this.resetTimers();

    console.log('[Phase 5] InactivityTracker started');
  }

  /**
   * Stop tracking user activity
   */
  stopTracking(): void {
    if (!this.isTracking) {
      return;
    }

    this.isTracking = false;
    this.clearTimers();

    // Remove activity listeners
    this.ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, this.handleActivity, true);
    });

    console.log('[Phase 5] InactivityTracker stopped');
  }

  /**
   * Reset inactivity timers (called on user activity or manual reset)
   */
  resetTimers(): void {
    if (!this.isTracking) {
      return;
    }

    this.clearTimers();

    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        console.log('[Phase 5] Inactivity warning triggered');
        if (this.onWarning) {
          this.onWarning();
        }
      });
    }, this.warningDuration);

    // Set timeout timer
    this.inactivityTimer = setTimeout(() => {
      this.ngZone.run(() => {
        console.log('[Phase 5] Inactivity timeout triggered');
        if (this.onTimeout) {
          this.onTimeout();
        }
      });
    }, this.timeoutDuration);
  }

  /**
   * Get current tracking status
   */
  isActive(): boolean {
    return this.isTracking;
  }

  /**
   * Handle activity event (arrow function to preserve 'this' context)
   */
  private handleActivity = (): void => {
    if (!this.isTracking) {
      return;
    }

    // Reset timers on any activity
    this.resetTimers();

    // Phase 6: Broadcast activity to other tabs
    this.crossTabSync.broadcastActivity();
  };

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }
}
